import React, { useMemo } from 'react'

import { Decorator } from '@storybook/react'

import { PlanType } from '$extensionSrc/__generated__/graphql'
import {
  AccessByPlatform,
  useAccessByPlatform,
} from '$extensionSrc/data/user/accessByPlatform'
import {
  AutoscanSetting,
  useAutoscanSettings,
} from '$extensionSrc/data/user/autoscanSettings'
import { logout } from '$extensionSrc/data/user/logout'
import { useNextAutoscanDate } from '$extensionSrc/data/user/nextAutoscanDate'
import { useTrial, UseTrialRv } from '$extensionSrc/data/user/trial'
import { useUpgrade } from '$extensionSrc/data/user/upgrade'
import { UpsellType, useUpsellType } from '$extensionSrc/data/user/upsellType'
import { User, useUser } from '$extensionSrc/data/user/user'
import { setWelcomed, useWelcomed } from '$extensionSrc/data/user/welcomed'

export type UserContextType = {
  accessByPlatform: AccessByPlatform | undefined
  autoscanSettings: AutoscanSetting[] | undefined
  nextAutoscanDate: Date | null | undefined
  setWelcomed: (welcomed: boolean) => Promise<void>
  logout: () => Promise<void>
  onUpgradeClick: () => void
  planType: PlanType | undefined
  trial: UseTrialRv | null | undefined
  upgradeUrl: string | undefined
  upsellType: UpsellType | null | undefined
  user: User | null | undefined
  welcomed: boolean | undefined
}

export const defaultUserContext: UserContextType = {
  accessByPlatform: undefined,
  autoscanSettings: undefined,
  setWelcomed,
  logout,
  onUpgradeClick: () => {},
  planType: undefined,
  nextAutoscanDate: undefined,
  trial: undefined,
  upgradeUrl: undefined,
  upsellType: undefined,
  user: undefined,
  welcomed: undefined,
}

export const UserContext =
  React.createContext<UserContextType>(defaultUserContext)

export default function UserContextProvider({ children }) {
  const user = useUser()
  const welcomed = useWelcomed()

  const accessByPlatform = useAccessByPlatform(user)
  const autoscanSettings = useAutoscanSettings(user)
  const nextAutoscanDate = useNextAutoscanDate(user)
  const upsellType = useUpsellType(user)
  const { onClick: onUpgradeClick, url: upgradeUrl } = useUpgrade(user)
  const trial = useTrial(user)

  const userContext: UserContextType = useMemo(() => {
    const uc: UserContextType = {
      accessByPlatform,
      autoscanSettings,
      logout,
      nextAutoscanDate,
      onUpgradeClick,
      setWelcomed,
      trial,
      planType: user?.planType,
      upgradeUrl,
      upsellType,
      user,
      welcomed,
    }
    return uc
  }, [
    accessByPlatform,
    autoscanSettings,
    nextAutoscanDate,
    onUpgradeClick,
    trial,
    upgradeUrl,
    upsellType,
    user,
    welcomed,
  ])

  return (
    <UserContext.Provider value={userContext}>{children}</UserContext.Provider>
  )
}

// eslint-disable-next-line react/function-component-definition
export const UserContextProviderDecorator: Decorator = (
  Story,
  { parameters: { userContext: incomingUserContext } },
) => {
  const user = useMemo(() => {
    if (incomingUserContext?.user !== undefined) {
      return incomingUserContext.user
    }
    const u: User = {
      email: 'pickle@meeper.net',
      hasActivePlan: true,
      id: '1',
      name: 'Pickle',
      planType: PlanType.StandardPlan,
      registeredOn: '2024-10-22T10:00:00.000Z',
    }
    return u
  }, [incomingUserContext?.user])

  // These values are purely dependent on user, so there's no need to ever
  // provide them in incomingUserContext
  const accessByPlatform = useAccessByPlatform(user)
  const autoscanSettings = useAutoscanSettings(user)
  const nextAutoscanDate = useNextAutoscanDate(user)
  const upsellType = useUpsellType(user)
  const { onClick: onUpgradeClick, url: upgradeUrl } = useUpgrade(user)
  const trial = useTrial(user)

  const userContext: UserContextType = useMemo(() => {
    const uc: UserContextType = {
      accessByPlatform,
      autoscanSettings,
      logout,
      nextAutoscanDate,
      onUpgradeClick,
      setWelcomed,
      trial,
      planType: user?.planType,
      upgradeUrl,
      upsellType,
      user,
      welcomed: incomingUserContext?.welcomed
        ? incomingUserContext.welcomed
        : false,
    }
    return uc
  }, [
    accessByPlatform,
    autoscanSettings,
    incomingUserContext?.welcomed,
    nextAutoscanDate,
    onUpgradeClick,
    trial,
    upgradeUrl,
    upsellType,
    user,
  ])

  return (
    <UserContext.Provider value={userContext}>
      <Story />
    </UserContext.Provider>
  )
}
